import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Copy, DeleteWarning, Forms, Modal, SaveButton, TitleBar, SystemText, Loading } from '@salesperformanceint/e4shared';
import { apiPatch, apiPost, apiDelete } from 'services';
import { setShowSuccess } from 'actions/ui';
import Files from 'components/admin/forms/files';
import Like from 'components/general/like';
import Assets from 'components/general/assets';
import TextClamp from 'components/textClamp';
import { e4Format, e4Formats } from 'helpers/dates';
import { uploadType } from 'helpers/azure';

export const Question = ({ question, show, onClose, onReload }) => {
    const dispatch = useDispatch();
    const [saving, setSaving] = React.useState(false);
    const [doAnswer, setDoAnswer] = React.useState(false);
    const [answer, setAnswer] = React.useState({});
    const [validAnswers, setValidAnswers] = React.useState([]);
    const [approved, setApproved] = React.useState(0);
    const [errors, setErrors] = React.useState({});
    const [values, setValues] = React.useState({});
    const [showDeleteWarning, setShowDeleteWarning] = React.useState(false);
    const { guid, id, administrator } = useSelector(state => state.user.user);
    const userId = useSelector(state => state.user.user.id);
    const { tags, handles } = useSelector(state => state.ui);

    React.useEffect(() => {
        const approved = question?.answers ? question.answers.filter(a => a.approved) : null;
        setApproved(approved && approved.length > 0 ? approved[0].id : 0);
    }, [question]);

    React.useEffect(() => {
        const temp = question?.answers ? question.answers.filter(a => !a.deleted) : [];
        setValidAnswers(temp);
    }, [question]);

    const saveComplete = () => {
        setErrors({});
        setSaving(false);
        dispatch(setShowSuccess(SystemText('KnowledgeBank/Question/Message/SaveSuccess')));
        setDoAnswer(false);
        onClose?.();
        onReload?.();
    };

    const saveFailed = e => {
        setErrors(e);
        setSaving(false);
    };

    const closeModal = () => {
        setErrors({});
        setValues({});
        setDoAnswer(false);
        onClose?.();
    };

    const deleteConfirm = confirm => {
        confirm &&
            apiDelete(
                `question-bank/answer/${answer.id}`,
                null,
                guid,
                () => {
                    onReload?.();
                    question.answers.filter(a => a.id === answer.id)[0].deleted = true;
                    const temp = question.answers.filter(a => !a.deleted);
                    setValidAnswers(temp);
                },
                null
            );
        setShowDeleteWarning(false);
    };

    const approve = id => {
        apiPost(
            `question-bank/approve/${id}`,
            null,
            guid,
            () => {
                onReload?.();
                setApproved(id);
            },
            null
        );
    };

    const onSave = () => {
        const saving = values;
        let tempErrors = errors;
        let noErrors = true;
        if (!saving.answer) {
            tempErrors = { ...tempErrors, answer: SystemText('KnowledgeBank/Question/Fields/Answer/Error') };
            noErrors = false;
        }

        const responses = {
            ok: response => {
                if (response.ok) {
                    saveComplete();
                } else {
                    tempErrors = { ...tempErrors, answer: SystemText('KnowledgeBank/Question/Fields/General/Error') };
                    saveFailed(tempErrors);
                }
            },
            error: error => {
                tempErrors = { ...tempErrors, answer: SystemText('KnowledgeBank/Question/Fields/General/Error') };
                saveFailed(tempErrors);
            },
        };

        if (noErrors) {
            saving.question_Id = question.id;
            if (saving.id) {
                apiPatch('question-bank/answer', saving, guid, responses.ok, responses.error);
            } else {
                apiPost('question-bank/answer', saving, guid, responses.ok, responses.error);
            }
        } else {
            setSaving(false);
            setErrors(tempErrors);
        }
    };

    const setValue = (prop, value) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const editor = owner => {
        return owner === id || administrator;
    };

    const footer = (
        <div>
            <SaveButton setSaving={setSaving} saving={saving} className="btn btn-success" onClick={() => onSave()}>
                {SystemText('KnowledgeBank/Question/Buttons/Save')}
            </SaveButton>
            <button className="btn" onClick={() => setDoAnswer(false)}>
                {SystemText('KnowledgeBank/Question/Buttons/Cancel')}
            </button>
        </div>
    );

    return (
        <Modal
            show={show}
            close={closeModal}
            title={SystemText('KnowledgeBank/Question/Modal/Title')}
            footer={doAnswer ? footer : null}
        >
            {!question ? (
                <Loading />
            ) : (
                <>
                    <div className="mb10 text-sm">{e4Format(question.created, e4Formats.long, true)}</div>
                    <h2 style={{ margin: 0 }}>{question.name}</h2>
                    <p className="mt10">
                        <TextClamp lines="10">
                            <Copy value={question.description} />
                        </TextClamp>
                    </p>
                    <div className="mt20 mb20">
                        <span className="">{`${SystemText('KnowledgeBank/Question/By')} `} </span>
                        <Link to={`/search/${question.owner_Full_Name}`}>{question.owner_Full_Name}</Link>
                    </div>
                    <Like entity="question" id={question.id} name="question" owner_Id={userId} onLike={onReload} />
                    {doAnswer ? (
                        <div className="mt20 pt10 white-title-bar">
                            <TitleBar title={SystemText('KnowledgeBank/Question/TitleBar')} />
                            <Forms.Textarea
                                tags={tags}
                                handles={handles}
                                error={errors.answer}
                                value={values.answer}
                                help={SystemText('KnowledgeBank/Question/Fields/Answer/Help')}
                                onChange={value => setValue('answer', value)}
                                label={SystemText('KnowledgeBank/Question/Fields/Answer/Label')}
                                rows="10"
                            />
                            <Files
                                value={values.assets}
                                onChange={value => setValue('assets', value)}
                                label={SystemText('KnowledgeBank/Question/Fields/Attachments/Label')}
                                help={SystemText('KnowledgeBank/Question/Fields/Attachments/Help')}
                                folder={uploadType.QuestionAnswer}
                                allowMultiple
                                modalInModal
                            />
                        </div>
                    ) : validAnswers.length > 0 ? (
                        <div className="mt10 pt10 onWhite">
                            <TitleBar
                                title={`${validAnswers.length} ${
                                    validAnswers?.length === 1
                                        ? SystemText('KnowledgeBank/Question/Answer')
                                        : SystemText('KnowledgeBank/Question/Answer/Plural')
                                }`}
                            />
                            {validAnswers.map((a, i) => {
                                return (
                                    <div
                                        key={`answer-${a.id}`}
                                        data-testid={a.id === approved ? 'approved-answer' : `answer-${i}`}
                                        className="answer mb10 p5"
                                        style={a.id === approved ? { backgroundColor: 'rgba(0, 166, 90, .1)' } : null}
                                    >
                                        <div className="flex flex-row">
                                            <div className="mb10 text-sm">{e4Format(a.created, e4Formats.long, true)}</div>
                                            <div className="mb5 text-sm flex-grow text-right">
                                                <span className="">{SystemText('KnowledgeBank/Question/By')} </span>
                                                <Link to={`/search/${a.owner_Full_Name}`}>{a.owner_Full_Name}</Link>
                                            </div>
                                        </div>
                                        <p>
                                            <TextClamp lines="5">
                                                <Copy value={a.answer} />
                                            </TextClamp>
                                        </p>
                                        {a.assets && <Assets assets={a.assets} />}
                                        <div className="flex flex-row">
                                            <Like entity="answer" id={a.id} name="answer" owner_Id={userId} />
                                            <div className="flex-grow text-right table-btns">
                                                {editor(question.owner_Id) && (
                                                    <button
                                                        className="btn btn-xs btn-success"
                                                        title={SystemText('KnowledgeBank/Question/Buttons/Approve/Title')}
                                                        disabled={a.id === approved}
                                                        onClick={() => approve(a.id)}
                                                    >
                                                        <i className="fa fa-check-circle mr5" />
                                                        {SystemText('KnowledgeBank/Question/Buttons/Approve')}
                                                    </button>
                                                )}
                                                {(editor(question.owner_Id) || editor(a.owner_Id)) && (
                                                    <span>
                                                        <button
                                                            onClick={() => {
                                                                setValues(a);
                                                                setDoAnswer(true);
                                                            }}
                                                            title={SystemText('KnowledgeBank/Question/Buttons/Edit/Title')}
                                                            className="btn btn-xs btn-primary"
                                                        >
                                                            <i className="fa fa-pencil" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setAnswer(a);
                                                                setShowDeleteWarning(true);
                                                            }}
                                                            title={SystemText('KnowledgeBank/Question/Buttons/Delete/Title')}
                                                            className="btn btn-xs btn-danger"
                                                        >
                                                            <i className="fa fa-trash" />
                                                        </button>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="clear-titles">
                            <TitleBar title={SystemText('KnowledgeBank/Question/NoAnswers/Title')} />
                            <div className="answer mb10 text-center">
                                <h3>{SystemText('KnowledgeBank/Question/NoAnswers/H3')}</h3>
                                <p>{SystemText('KnowledgeBank/Question/NoAnswers/Message', question?.owner_Full_Name)}</p>
                                <p>{SystemText('KnowledgeBank/Question/NoAnswers/Thanks')}</p>
                            </div>
                        </div>
                    )}
                    {!doAnswer && (
                        <div className="mt20">
                            <button onClick={() => setDoAnswer(true)} className="btn btn-block btn-success">
                                {SystemText('KnowledgeBank/Question/Buttons/SubmitAnswer')}
                            </button>
                        </div>
                    )}
                    <DeleteWarning
                        id="deleteWarning"
                        message={SystemText('KnowledgeBank/Question/Delete/Warning')}
                        show={showDeleteWarning}
                        close={confirm => deleteConfirm(confirm)}
                    />
                </>
            )}
        </Modal>
    );
};

export default withRouter(Question);
